import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Loader2, LogOut, Trash2 } from 'lucide-react';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useLoginActions } from '@/hooks/useLoginActions';
import { useWorkspace } from '@/hooks/useWorkspace';
import { useVault } from '@/hooks/useVault';
import * as storage from '@/lib/workspace/storage';
import { clearProviderSecretsStorage } from '@/lib/crypto/secrets';
import {
  lockVault,
  setSealEnabled,
  setVaultConfigured,
} from '@/lib/crypto/vault';
import { OpenDorkWordmark } from './OpenDorkWordmark';

function shortHex(hex: string): string {
  if (hex.length < 16) return hex;
  return `${hex.slice(0, 8)}…${hex.slice(-6)}`;
}

/**
 * Blocks the workspace when the logged-in pubkey does not match the
 * browser workspace owner (or vault.pubkey). Prevents cross-account data bleed
 * on a shared machine / multi-login browser profile.
 */
export function AccountIsolationGate({ children }: { children: React.ReactNode }) {
  const { user } = useCurrentUser();
  const { logout } = useLoginActions();
  const { settings, ready, refresh, updateSettings } = useWorkspace();
  const { hasVault, lock } = useVault();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const owner =
    settings.ownerPubkey || settings.vault?.pubkey || undefined;
  const mismatch =
    !!user && !!owner && owner.toLowerCase() !== user.pubkey.toLowerCase();

  // Bind owner on first use when unset
  useEffect(() => {
    if (!ready || !user || mismatch) return;
    if (settings.ownerPubkey) return;
    void updateSettings({ ownerPubkey: user.pubkey });
  }, [ready, user, mismatch, settings.ownerPubkey, updateSettings]);

  const handleSwitchAccount = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      lock();
      await logout();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Logout failed');
    } finally {
      setBusy(false);
    }
  }, [lock, logout]);

  const handleClearLocal = useCallback(async () => {
    if (
      !confirm(
        'Permanently delete ALL local OpenDork data on this browser (projects, chats, vault, API keys)? This cannot be undone. Remote relay snapshots are not deleted.',
      )
    ) {
      return;
    }
    const typed = window.prompt('Type DELETE to confirm wipe of local data:');
    if (typed !== 'DELETE') return;

    setBusy(true);
    setError(null);
    try {
      // Disable seal before wipe — fail-closed seal would refuse plaintext writes
      lock();
      lockVault();
      setSealEnabled(false);
      setVaultConfigured(false);
      await clearProviderSecretsStorage();
      // Empty workspace under the *current* login
      const emptySettings = {
        ...settings,
        ownerPubkey: user?.pubkey,
        vault: undefined,
        encryptLocal: false,
        activeProjectId: undefined,
        activeChatId: undefined,
        activeChatByProject: {},
        globalInstructions: '',
        syncLastPublishedAt: undefined,
        syncLastPulledAt: undefined,
        syncAutoPullDoneKey: undefined,
      };
      await storage.replaceWorkspace({
        settings: emptySettings,
        projects: [],
        chats: [],
        messages: [],
      });
      // Also clear app-config providers (public list)
      try {
        const raw = localStorage.getItem('nostr:app-config');
        if (raw) {
          const cfg = JSON.parse(raw) as Record<string, unknown>;
          cfg.aiProviders = [];
          localStorage.setItem('nostr:app-config', JSON.stringify(cfg));
          window.dispatchEvent(new Event('opendork:providers-restored'));
        }
      } catch {
        // ignore
      }
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Clear failed');
    } finally {
      setBusy(false);
    }
  }, [lock, settings, user?.pubkey, refresh]);

  if (!user || !ready || !mismatch) {
    return <>{children}</>;
  }

  return (
    <div className="min-h-dvh bg-[#0a0a0a] text-[#e8e6e3] flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-md flex flex-col items-center gap-5">
        <OpenDorkWordmark className="w-40 text-[#B7B1B1]" />
        <div className="flex items-center gap-2 text-yellow-500/90">
          <AlertTriangle className="w-4 h-4" />
          <span className="text-sm font-medium">Different account</span>
        </div>
        <p className="text-[13px] text-[#8a8884] text-center leading-relaxed">
          This browser already has an OpenDork workspace bound to another Nostr
          identity. Mixing accounts would risk leaking chats and keys across
          logins.
        </p>
        <div className="w-full rounded-xl border border-[#2a2a2a] bg-[#111] px-4 py-3 space-y-2 text-[12px]">
          <div className="flex justify-between gap-2">
            <span className="text-[#5a5a5a]">Workspace owner</span>
            <span className="text-[#c8c6c3] font-mono text-[11px]">{shortHex(owner!)}</span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-[#5a5a5a]">Logged in as</span>
            <span className="text-[#c8c6c3] font-mono text-[11px]">{shortHex(user.pubkey)}</span>
          </div>
          {hasVault && (
            <p className="text-[10px] text-[#3a3a3a] pt-1">
              A local vault is configured for the owner account.
            </p>
          )}
        </div>
        <div className="w-full flex flex-col gap-2">
          <button
            type="button"
            onClick={() => void handleSwitchAccount()}
            disabled={busy}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium bg-[#e8e6e3] text-[#0a0a0a] rounded-xl disabled:opacity-50"
          >
            {busy ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <LogOut className="w-4 h-4" />
            )}
            Log out — switch to owner account
          </button>
          <button
            type="button"
            onClick={() => void handleClearLocal()}
            disabled={busy}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm border border-red-900/50 text-red-400/90 rounded-xl hover:bg-red-950/20 disabled:opacity-50"
          >
            <Trash2 className="w-4 h-4" />
            Wipe local data for this browser
          </button>
        </div>
        {error && <p className="text-[12px] text-red-400 text-center">{error}</p>}
        <p className="text-[10px] text-[#3a3a3a] text-center leading-relaxed">
          Wiping only affects this browser. Relay sync snapshots stay until you
          publish an empty one from the owner account.
        </p>
      </div>
    </div>
  );
}
